/**
 * Loads the TrueMed product-page widget script at most once per page.
 */
window.ensureTruemedWidgetScript = function ensureTruemedWidgetScript() {
  if (document.getElementById('truemed-product-page-widget')) return;

  const script = document.createElement('script');
  script.id = 'truemed-product-page-widget';
  script.src = 'https://static.truemed.com/widgets/product-page-widget.min.js';
  script.defer = true;
  document.head.appendChild(script);
};

/**
 * Force-close the quickshop drawer (isOpen flag can be out of sync).
 * Lets the CSS slide-out finish before content is cleared.
 */
window.closeQuickshopDrawer = function closeQuickshopDrawer() {
  const drawer = document.getElementById('quickshop-drawer');
  if (!drawer) return;

  const alreadyClosed = !drawer.classList.contains('open') && !drawer.isOpen;
  drawer.removeAttribute('data-opened-from-cart');
  // A second close (cart:update / fetch patch) must not hideOverlay and kill the cart
  if (alreadyClosed) return;

  // Ensure closeDrawer() does not early-return when flag is stale
  drawer.isOpen = true;

  if (typeof drawer.closeDrawer === 'function') {
    try {
      drawer.closeDrawer();
      return;
    } catch (err) {
      console.warn('quickshop closeDrawer failed', err);
    }
  }

  // Fallback if closeDrawer is unavailable
  drawer.classList.remove('open');
  drawer.setAttribute('inert', '');
  drawer.setAttribute('aria-hidden', 'true');
  drawer.isOpen = false;

  if (typeof drawer.clearQuickshopBodyAfterTransition === 'function') {
    drawer.clearQuickshopBodyAfterTransition();
  } else {
    const body = drawer.querySelector('[data-quickshop-body]');
    if (body) {
      setTimeout(() => {
        if (!drawer.classList.contains('open')) body.innerHTML = '';
      }, 350);
    }
  }
};

/**
 * QuickShop Modal Component
 * Manages quick shop modal/drawer for product quick view
 * @class
 */
class QuickShopModal {
  constructor() {
    this.drawer = Utility.DomSelector.byId('quickshop-drawer');
    this.body = Utility.DomSelector.$('[data-quickshop-body]', this.drawer);
    this.bindCartClose();
    this.bindFetchClose();
  }

  /**
   * Close quickshop whenever an item is added to cart (theme event bus)
   */
  bindCartClose() {
    if (typeof eventBus === 'undefined' || typeof THEME_EVENTS === 'undefined') return;

    eventBus.subscribe(THEME_EVENTS.cartUpdate, ({ data } = {}) => {
      if (data?.action !== 'add-item') return;
      window.closeQuickshopDrawer();
    });
  }

  /**
   * Close quickshop after any successful /cart/add request while drawer is open.
   * Covers ProductForm and any other ATC path.
   */
  bindFetchClose() {
    if (window.__quickshopFetchPatched) return;
    window.__quickshopFetchPatched = true;

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      try {
        const request = args[0];
        const url = typeof request === 'string' ? request : request?.url || '';
        const drawer = document.getElementById('quickshop-drawer');
        const quickshopOpen = drawer && (drawer.classList.contains('open') || drawer.isOpen);

        if (
          quickshopOpen &&
          response.ok &&
          /\/cart\/add(\.js)?(\?|$)/.test(url)
        ) {
          // Defer so ProductForm can finish publishing cart:update first.
          // Skip if ProductForm already closed the drawer — hideOverlay would close the cart.
          setTimeout(() => {
            const qs = document.getElementById('quickshop-drawer');
            if (!qs || (!qs.classList.contains('open') && !qs.isOpen)) return;
            window.closeQuickshopDrawer();
          }, 0);
        }
      } catch (err) {
        // Never break ATC because of close helper
      }

      return response;
    };
  }

  /**
   * Re-bind close controls injected with quickshop HTML
   * (drawer constructor only queried empty markup on page load)
   */
  bindInjectedCloseButtons(drawer) {
    if (!drawer) return;
    Utility.DomSelector.$$('[data-closedrawer]', drawer).forEach((btn) => {
      if (btn.dataset.quickshopCloseBound === 'true') return;
      btn.dataset.quickshopCloseBound = 'true';
      btn.addEventListener('click', (evt) => {
        evt.preventDefault();
        window.closeQuickshopDrawer();
      });
    });
  }

  /**
   * Opens QuickShop modal and loads product content
   * @param {HTMLElement} element - The element that triggered the quickshop
   * @returns {Promise<Object>} Promise resolving to {success: boolean, data: any, error: string, drawer: HTMLElement}
   */
  async openQuickShop(element) {
    try{
      const handle = element.dataset.handle;
      if (!handle || typeof handle !== 'string') {
        console.warn('Invalid handle provided to openQuickShop');
        return { success: false, error: 'Invalid product handle' };
      }
      
      const drawer = Utility.DomSelector.byId('quickshop-drawer');
      const body = Utility.DomSelector.$('[data-quickshop-body]', drawer);

      if (!body || !(body instanceof HTMLElement)) {
        console.warn('Quickshop body element not found');
        return { success: false, error: 'Quickshop body element not found' };
      }

      let quickviewTempHandle = 'quickview';
      if(element.closest(`[data-pair-with-items]`)){
        quickviewTempHandle = 'quickview2';
      }
      const quickShopData = await Utility.Fetch.product(handle, quickviewTempHandle);
      if (!quickShopData) {
        throw new Error('No data received from quickshop request');
      }

      body.innerHTML = quickShopData;
      const productTitle = element.dataset.title;
      if (productTitle) {
        drawer.setAttribute('aria-label', `Quickshop: ${productTitle}`);
      }
      if (element.closest('#component-cart-drawer')) {
        drawer.setAttribute('data-opened-from-cart', 'true');
      } else {
        drawer.removeAttribute('data-opened-from-cart');
      }
      drawer.openDrawer(element);
      this.bindInjectedCloseButtons(drawer);
      lazyImageObserver.observe();
      
      // Trigger swiper load if swiper-container is present in the loaded content
      // This ensures swiper bundle loads even if it wasn't present on initial page load
      if (typeof window.triggerSwiperLoad === 'function') {
        window.triggerSwiperLoad();
      }

      // Re-bind Loop subscription cards injected via innerHTML
      document.dispatchEvent(new CustomEvent('quickshop:loaded', { detail: { drawer } }));

      // TrueMed widget mount is injected via innerHTML — load script once if needed
      if (body.querySelector('#truemed-instructions')) {
        window.ensureTruemedWidgetScript();
      }
      
      return { success: true, data: quickShopData, drawer: drawer };
    }catch(error){
      console.error('Error opening quickshop:', error);
      Utility.handleError(error.message, 'Quickshop Error');
      return { success: false, error: error.message };
    }
  }
}
new QuickShopModal();

/**
 * QuickShop Button Component
 * Button element that triggers quick shop modal
 * @extends HTMLElement
 */
class QuickShopButton extends HTMLElement {
  constructor() {
    super();

    this.boundClickHandler = this.handleClick.bind(this);
  }

  /**
   * Lifecycle callback when element is connected to DOM
   */
  connectedCallback() {
    this.addEventListener('click', this.boundClickHandler);
  }

  /**
   * Handles click event to open quick shop
   */
  handleClick() {
    QuickShopModal.prototype.openQuickShop(this);
  }

  /**
   * Lifecycle callback when element is disconnected from DOM
   */
  disconnectedCallback() {
    this.removeEventListener('click', this.boundClickHandler);
  }
}
customElements.define("quick-shop-button", QuickShopButton);
